import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { SeoPageView } from "@/components/seo-page-view";
import { THEMES } from "@/lib/seo-routes";
import { findAirport } from "@/lib/airports";
import { loadSeoPage, buildSeoPageHead, triggerSeoGeneration } from "@/lib/seo-page-loader";
import { buildSearchHref } from "@/lib/search-href";
import { getInitialOrigin } from "@/lib/initial-origin.functions";
import { breadcrumbScript } from "@/lib/breadcrumbs";
import { faqScript } from "@/lib/faq-schema";
import { setEdgeCache } from "@/lib/ssr-cache";

export const Route = createFileRoute("/loty/temat/$slug")({
  loader: async ({ params }) => {
    const theme = THEMES.find((t) => t.slug === params.slug);
    if (!theme) throw notFound();
    await setEdgeCache();
    // SSR: SEO page + geo-hub usera, żeby SearchForm na landingu tematycznym
    // (np. „loty na narty") miał `initial.from` z lokalizacji CF od pierwszej
    // klatki — bez migotania WAR → KRK po hydratacji.
    const [page, origin] = await Promise.all([
      loadSeoPage({
        type: "theme", slug: params.slug, label: theme.label, description: theme.description,
      }),
      getInitialOrigin({ data: { page: "/loty/temat" } }),
    ]);
    const today = new Date(); today.setUTCHours(0,0,0,0);
    const dd = new Date(today.getTime() + 30 * 86400_000).toISOString().slice(0, 10);
    const rd = new Date(today.getTime() + 37 * 86400_000).toISOString().slice(0, 10);
    const fromName = findAirport(origin.from)?.city;
    return { slug: params.slug, theme, page, dd, rd, initialFrom: origin.from, fromName };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const { slug, theme, page } = loaderData;
    const path = `/loty/temat/${slug}`;
    const head = buildSeoPageHead(
      page,
      path,
      `${theme.label} | Tanie Latanie`,
      theme.description
    );
    const breadcrumb = breadcrumbScript([
      { name: "Strona główna", path: "/" },
      { name: "Loty", path: "/loty" },
      { name: theme.label, path },
    ]);
    const faqLd = page?.faq ? faqScript(page.faq) : null;
    const scripts = [...(head.scripts ?? []), breadcrumb];
    if (faqLd) scripts.push(faqLd);
    return { ...head, scripts };
  },
  component: ThemePage,
});

function ThemePage() {
  const { slug, theme, page, dd, rd, initialFrom, fromName } = Route.useLoaderData();
  useEffect(() => {
    if (!page) triggerSeoGeneration({ type: "theme", slug, label: theme.label, description: theme.description });
  }, [page, slug, theme.label, theme.description]);
  return (
    <SeoPageView
      type="theme"
      from={initialFrom}
      fromName={fromName}
      dd={dd}
      rd={rd}
      adults={1}
      page={page}
      themeSlug={slug}
      fallbackH1={theme.label}
      searchHref={buildSearchHref({ from: initialFrom, to: "BCN", dd, rd, adults: 1 })}
    />
  );
}
